import React, { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { 
  BarChart3, 
  PieChart,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  Filter,
  History
} from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  Line,
  Area,
  Cell,
  PieChart as RechartsPieChart,
  Pie
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

// Get auth headers
const getAuthHeaders = () => {
  const token = sessionStorage.getItem("prosperMindToken") || sessionStorage.getItem("authToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Format currency
const formatCurrency = (amount, currency = "USD") => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

// Category colors
const CATEGORY_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', 
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
];

// Expense type colors
const TYPE_COLORS = {
  'fixed': '#3B82F6',
  'variable': '#10B981',
  'discretionary': '#F59E0B',
  'debt': '#EF4444',
  'savings': '#8B5CF6'
};

// Payment method colors
const PAYMENT_COLORS = {
  'cash': '#10B981',
  'credit_card': '#EF4444',
  'debit_card': '#3B82F6',
  'bank_transfer': '#8B5CF6',
  'other': '#6B7280'
};

const AnalyticsModule = () => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [displayCurrency, setDisplayCurrency] = useState('USD');
  const [exchangeRates, setExchangeRates] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  
  // Filter state
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  
  // Analytics data
  const [analyticsData, setAnalyticsData] = useState({
    monthlyTrends: [],
    categoryBreakdown: [],
    typeBreakdown: [],
    paymentMethodBreakdown: []
  });

  // Fetch user profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API}/api/user/profile`, {
          headers: getAuthHeaders()
        });
        setUserProfile(response.data);
        if (response.data?.currencies?.length > 0) {
          const primaryCurrency = response.data.currencies.find(c => c.is_primary);
          setDisplayCurrency(primaryCurrency?.code || response.data.currencies[0].code);
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };
    fetchProfile();
  }, []);

  // Fetch expenses
  useEffect(() => {
    const fetchExpenses = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${API}/api/expenses`, {
          headers: getAuthHeaders()
        });
        // Ensure expenses is always an array
        const expenseData = response.data;
        if (Array.isArray(expenseData)) {
          setExpenses(expenseData);
        } else if (expenseData && Array.isArray(expenseData.expenses)) {
          setExpenses(expenseData.expenses);
        } else {
          setExpenses([]);
        }
      } catch (error) {
        console.error("Error fetching expenses:", error);
        setExpenses([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchExpenses();
  }, []);

  // Fetch exchange rates
  useEffect(() => {
    const fetchRates = async () => {
      if (!displayCurrency) return;
      
      setLoadingRates(true);
      try {
        const response = await axios.get(
          `https://api.exchangerate-api.com/v4/latest/${displayCurrency}`
        );
        setExchangeRates({ rates: response.data.rates, base: displayCurrency });
      } catch (error) {
        console.error("Error fetching exchange rates:", error);
        // Use fallback rates
        setExchangeRates({
          rates: { USD: 1, EUR: 0.85, GBP: 0.73, JPY: 110, MXN: 17 },
          base: 'USD',
          fallback: true
        });
      } finally {
        setLoadingRates(false);
      }
    };
    fetchRates();
  }, [displayCurrency]);

  // Process analytics data
  useEffect(() => {
    if (expenses.length === 0) {
      setAnalyticsData({
        monthlyTrends: [],
        categoryBreakdown: [],
        typeBreakdown: [],
        paymentMethodBreakdown: []
      });
      return;
    }

    // Filter expenses for selected month
    const monthFilteredExpenses = expenses.filter(exp => {
      const expDate = new Date(exp.date);
      return expDate.getMonth() + 1 === selectedMonth && 
             expDate.getFullYear() === selectedYear;
    });

    // Monthly trends for the year
    const monthlyData = {};
    const prevYearData = {};
    
    for (let m = 1; m <= 12; m++) {
      monthlyData[m] = 0;
      prevYearData[m] = 0;
    }

    expenses.forEach(exp => {
      const date = new Date(exp.date);
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      
      if (year === selectedYear) {
        monthlyData[month] += exp.amount;
      } else if (year === selectedYear - 1) {
        prevYearData[month] += exp.amount;
      }
    });

    const monthlyTrends = Object.entries(monthlyData).map(([month, amount]) => ({
      month: new Date(selectedYear, parseInt(month) - 1).toLocaleDateString(
        i18n.language === 'es' ? 'es-ES' : 'en-US', 
        { month: 'short' }
      ),
      expenses: amount,
      prevYearExpenses: prevYearData[parseInt(month)],
      target: 5000, // Could be made dynamic from budget
      isCurrentMonth: parseInt(month) === new Date().getMonth() + 1 && selectedYear === new Date().getFullYear()
    }));

    // Category breakdown for selected month
    const categoryTotals = {};
    monthFilteredExpenses.forEach(exp => {
      const category = exp.category || 'Other';
      categoryTotals[category] = (categoryTotals[category] || 0) + exp.amount;
    });

    const categoryBreakdown = Object.entries(categoryTotals).map(([name, value], index) => ({
      name,
      value,
      color: CATEGORY_COLORS[index % CATEGORY_COLORS.length]
    }));

    // Type breakdown
    const typeTotals = {};
    monthFilteredExpenses.forEach(exp => {
      const type = exp.type || 'variable';
      typeTotals[type] = (typeTotals[type] || 0) + exp.amount;
    });

    const typeBreakdown = Object.entries(typeTotals).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1).replace('_', ' '),
      value,
      color: TYPE_COLORS[name] || '#6B7280'
    }));

    // Payment method breakdown
    const paymentTotals = {};
    monthFilteredExpenses.forEach(exp => {
      const method = exp.payment_method || 'other';
      paymentTotals[method] = (paymentTotals[method] || 0) + exp.amount;
    });

    const paymentMethodBreakdown = Object.entries(paymentTotals).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1).replace('_', ' '),
      value,
      color: PAYMENT_COLORS[name] || '#6B7280'
    }));

    setAnalyticsData({
      monthlyTrends,
      categoryBreakdown,
      typeBreakdown,
      paymentMethodBreakdown
    });
  }, [expenses, selectedMonth, selectedYear, i18n.language]);

  // Filter expenses for current view (memoized)
  const filteredExpenses = React.useMemo(() => {
    return expenses.filter(exp => {
      const expDate = new Date(exp.date);
      return expDate.getMonth() + 1 === selectedMonth && 
             expDate.getFullYear() === selectedYear;
    });
  }, [expenses, selectedMonth, selectedYear]);

  // Calculate total in display currency
  const calculateTotalInDisplayCurrency = (expenseList) => {
    if (!exchangeRates?.rates) return expenseList.reduce((sum, exp) => sum + exp.amount, 0);
    
    return expenseList.reduce((sum, exp) => {
      const expCurrency = exp.currency || 'USD';
      if (expCurrency === displayCurrency) {
        return sum + exp.amount;
      }
      // Convert
      const rate = exchangeRates.rates[expCurrency];
      if (rate) {
        return sum + (exp.amount / rate);
      }
      return sum + exp.amount;
    }, 0);
  };

  // Get available years from expenses
  const availableYears = [...new Set(expenses.map(exp => new Date(exp.date).getFullYear()))].sort((a, b) => b - a);
  if (!availableYears.includes(new Date().getFullYear())) {
    availableYears.unshift(new Date().getFullYear());
  }

  const totalExpenses = calculateTotalInDisplayCurrency(filteredExpenses);
  const pieChartRadius = 100;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-black rounded-2xl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading expense history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-black p-6 rounded-2xl" data-testid="expense-history-module">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <History className="w-8 h-8 text-cyan-500" />
            Expense History
          </h1>
          <p className="text-gray-400 mt-1">Track and analyze your spending patterns</p>
        </div>
        
        {/* Period Selector */}
        <div className="flex gap-3">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            className="px-4 py-2 border border-gray-700 rounded-xl bg-gray-800 text-white focus:border-cyan-500 focus:ring-0"
          >
            {[...Array(12)].map((_, i) => (
              <option key={i + 1} value={i + 1}>
                {new Date(2024, i).toLocaleDateString('en-US', { month: 'long' })}
              </option>
            ))}
          </select>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-4 py-2 border border-gray-700 rounded-xl bg-gray-800 text-white focus:border-cyan-500 focus:ring-0"
          >
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Currency Summary Card */}
      <div className="bg-gradient-to-r from-cyan-600 to-blue-700 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold mb-1 text-cyan-100">Total Expenses</h3>
            <p className="text-4xl font-bold">
              {formatCurrency(totalExpenses, displayCurrency)}
            </p>
            <p className="text-cyan-200 text-sm mt-1">
              {filteredExpenses.length} transactions in {new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              {exchangeRates?.fallback && <span className="ml-2 text-yellow-300">(using fallback rates)</span>}
            </p>
          </div>
          
          {/* Currency Selector */}
          <div className="flex flex-col gap-2">
            <label className="text-cyan-200 text-sm">Display in:</label>
            <select
              value={displayCurrency}
              onChange={(e) => setDisplayCurrency(e.target.value)}
              disabled={loadingRates}
              className="bg-white/20 backdrop-blur border border-white/30 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-white/50 cursor-pointer min-w-[160px]"
            >
              {userProfile?.currencies?.map((curr) => (
                <option key={curr.code} value={curr.code} className="text-gray-900">
                  {curr.code} {curr.is_primary ? '(Primary)' : ''}
                </option>
              ))}
              <option value="USD" className="text-gray-900">USD</option>
              <option value="EUR" className="text-gray-900">EUR</option>
              <option value="GBP" className="text-gray-900">GBP</option>
            </select>
            {loadingRates && (
              <span className="text-cyan-200 text-xs">Loading rates...</span>
            )}
          </div>
        </div>
        
        {/* Currency breakdown */}
        {filteredExpenses.length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/20">
            <p className="text-cyan-200 text-sm mb-2">Breakdown by original currency:</p>
            <div className="flex flex-wrap gap-3">
              {Object.entries(
                filteredExpenses.reduce((acc, exp) => {
                  const curr = exp.currency || 'USD';
                  acc[curr] = (acc[curr] || 0) + exp.amount;
                  return acc;
                }, {})
              ).map(([currency, amount]) => (
                <div key={currency} className="bg-white/10 rounded-lg px-3 py-1">
                  <span className="font-medium">{formatCurrency(amount, currency)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Monthly Trends Chart */}
      <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800 shadow-sm">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-cyan-500" />
          Monthly Trends ({selectedYear})
        </h3>
        <div className="w-full h-80">
          {analyticsData.monthlyTrends.length > 0 && analyticsData.monthlyTrends.some(m => m.expenses > 0) ? (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={analyticsData.monthlyTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fill: '#9ca3af', fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  tick={{ fill: '#9ca3af', fontSize: 12 }}
                  tickFormatter={(value) => formatCurrency(value, displayCurrency)}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    padding: '12px',
                    color: 'white'
                  }}
                  formatter={(value, name) => {
                    const labels = {
                      expenses: 'Expenses',
                      target: 'Target',
                      prevYearExpenses: 'Previous Year'
                    };
                    return [formatCurrency(value, displayCurrency), labels[name] || name];
                  }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  formatter={(value) => {
                    const labels = {
                      expenses: 'Expenses',
                      target: 'Target',
                      prevYearExpenses: 'Previous Year'
                    };
                    return <span className="text-gray-300">{labels[value] || value}</span>;
                  }}
                />
                {/* Previous Year Shadow Area */}
                <Area 
                  type="monotone"
                  dataKey="prevYearExpenses" 
                  name="prevYearExpenses"
                  fill="#64748b"
                  stroke="none"
                  fillOpacity={0.3}
                />
                {/* Current Year Bars */}
                <Bar 
                  dataKey="expenses" 
                  name="expenses"
                  radius={[8, 8, 0, 0]}
                >
                  {analyticsData.monthlyTrends.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.isCurrentMonth ? '#06b6d4' : '#0891b2'} 
                    />
                  ))}
                </Bar>
                {/* Target Line */}
                <Line 
                  type="monotone" 
                  dataKey="target" 
                  stroke="#ef4444" 
                  strokeWidth={2}
                  name="target"
                  dot={{ fill: '#ef4444', r: 3 }}
                  strokeDasharray="5 5"
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                <p className="text-gray-400">No expense data available</p>
                <p className="text-sm text-gray-500 mt-2">Add expenses to see trends</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Pie Charts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Category Breakdown */}
        <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800 shadow-sm">
          <h3 className="text-lg font-semibold text-white mb-1">By Category</h3>
          <p className="text-sm text-gray-500 mb-4">
            {new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </p>
          <div className="w-full h-64">
            {analyticsData.categoryBreakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={analyticsData.categoryBreakdown}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ percent }) => percent > 0.07 ? `${(percent * 100).toFixed(0)}%` : null}
                    outerRadius={pieChartRadius}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {analyticsData.categoryBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => formatCurrency(value, displayCurrency)}
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: 'white' }}
                  />
                  <Legend 
                    iconType="circle"
                    formatter={(value) => <span className="text-sm text-gray-300">{value}</span>}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <PieChart className="w-10 h-10 mx-auto mb-2 text-gray-600" />
                  <p className="text-gray-500 text-sm">No data</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Type Breakdown */}
        <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800 shadow-sm">
          <h3 className="text-lg font-semibold text-white mb-1">By Type</h3>
          <p className="text-sm text-gray-500 mb-4">
            {new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </p>
          <div className="w-full h-64">
            {analyticsData.typeBreakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={analyticsData.typeBreakdown}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ percent }) => percent > 0.07 ? `${(percent * 100).toFixed(0)}%` : null}
                    outerRadius={pieChartRadius}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {analyticsData.typeBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => formatCurrency(value, displayCurrency)}
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: 'white' }}
                  />
                  <Legend 
                    iconType="circle"
                    formatter={(value) => <span className="text-sm text-gray-300">{value}</span>}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <PieChart className="w-10 h-10 mx-auto mb-2 text-gray-600" />
                  <p className="text-gray-500 text-sm">No data</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Payment Method Breakdown */}
        <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800 shadow-sm">
          <h3 className="text-lg font-semibold text-white mb-1">By Payment Method</h3>
          <p className="text-sm text-gray-500 mb-4">
            {new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </p>
          <div className="w-full h-64">
            {analyticsData.paymentMethodBreakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={analyticsData.paymentMethodBreakdown}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ percent }) => percent > 0.07 ? `${(percent * 100).toFixed(0)}%` : null}
                    outerRadius={pieChartRadius}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {analyticsData.paymentMethodBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => formatCurrency(value, displayCurrency)}
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: 'white' }}
                  />
                  <Legend 
                    iconType="circle"
                    formatter={(value) => <span className="text-sm text-gray-300">{value}</span>}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <PieChart className="w-10 h-10 mx-auto mb-2 text-gray-600" />
                  <p className="text-gray-500 text-sm">No data</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsModule;
